<script>
import { mapGetters } from 'vuex'
import Request from '@src/mixins/request'
import PromotionForm from '@src/forms/Promotion'
import dropdown from '@src/data/dropdown.json'

const hideFields = {
  BASIC_DEAL: ['minAdvance', 'maxAdvance'],
  LAST_MINUTE: ['sellingDate', 'sellingTime', 'sellingDay', 'minAdvance'],
  EARLY_BIRD: ['sellingDate', 'sellingTime', 'sellingDay', 'maxAdvance'],
  LIMITED_TIME: ['minAdvance', 'maxAdvance'],
  '24H_DEAL': ['sellingTime', 'sellingDay', 'minAdvance', 'maxAdvance'],
  MINIMUM_STAY: ['minAdvance', 'maxAdvance'],
}

export default {
  metaInfo: {
    title: 'Promotion Detail',
    meta: [{ name: 'description', content: 'Promotion Detail' }],
  },

  $_veeValidate: { validator: 'new' },

  mixins: [Request],

  props: {
    ratePlans: {
      type: Array,
      required: true,
    },
    // roomTypes: {
    //   type: Array,
    //   required: true,
    // },
    cancellations: {
      type: Array,
      required: true,
    },
    dayOfWeek: {
      type: Array,
      required: true,
    },
    promotionTypes: {
      type: Array,
      required: true,
    },
    item: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      form: new PromotionForm(),
      color: 'gold',
      schema: PromotionForm.validator(),
      dropdown,
    }
  },

  computed: {
    ...mapGetters('app', ['propertyId']),
    isNew() {
      return this.$route.params.promotionId === 'new'
    },
    text() {
      return {
        title: this.isNew ? 'New Promotion' : 'Edit Promotion',
        submit: this.isNew ? 'Save' : 'Update',
      }
    },
    promoType() {
      return this.isNew ? this.$route.query.type : this.item.type
    },
  },

  mounted() {
    if (this.isNew) {
      this.form = new PromotionForm({}, this.$route.query.type)
    } else {
      this.form = new PromotionForm(this.item)
    }
  },

  methods: {
    isHiddenField(field) {
      return !hideFields[this.promoType].includes(field)
    },
    onSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.isNew) return this.addItem()

          return this.updateItem()
        }
      })
    },
    addItem() {
      this.updateData({
        url: this.endpoint.promotion.add,
        data: {
          propertyId: this.propertyId,
          data: this.form,
        },
      }).then((res) => {
        if (res) this.backToList()
      })
    },
    updateItem() {
      this.updateData({
        url: this.endpoint.promotion.update,
        data: {
          id: this.item.id,
          propertyId: this.propertyId,
          update: this.form.updatePayload(),
        },
      }).then((res) => {
        if (res) this.backToList()
      })
    },
    backToList() {
      this.$router.push({ name: 'promotion' })
    },
  },
}
</script>

<template>
  <VForm @submit.prevent="onSubmit">
    <BaseView :title="text.title" :color="color">
      <VLayout row wrap>
        <!-- Basic Info -->
        <VFlex class="mb-4" xs12>
          <div class="title mb-2">Basic Info</div>

          <BaseControlInput label="Promotion name">
            <VTextField
              v-model="form.name"
              v-validate="schema.name"
              data-vv-name="promotion name"
              label="Promotion name"
              :error-messages="errors.collect('promotion name')"
              :color="color"
            />
          </BaseControlInput>
        </VFlex>

        <!-- Sell Conditions -->
        <VFlex
          v-if="!['LAST_MINUTE', 'EARLY_BIRD'].includes(promoType)"
          class="mb-4"
          xs12
          sm6
        >
          <div class="title mb-2">
            Sell Conditions
          </div>

          <BaseControlInput
            v-if="isHiddenField('sellingDate')"
            label="Selling from"
          >
            <BaseDatePicker
              v-model="form.sellStartDate"
              v-validate="schema.sellStartDate"
              data-vv-name="selling from"
              label="Selling from"
              :error-messages="errors.collect('selling from')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput
            v-if="isHiddenField('sellingDate')"
            label="Selling to"
          >
            <BaseDatePicker
              v-model="form.sellEndDate"
              v-validate="schema.sellEndDate"
              data-vv-name="selling to"
              label="Selling to"
              :error-messages="errors.collect('selling to')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput
            v-if="isHiddenField('sellingTime')"
            label="Selling time from"
          >
            <BaseTimePicker
              v-model="form.sellStartTime"
              v-validate="schema.sellStartTime"
              data-vv-name="selling time from"
              label="Selling time from"
              :error-messages="errors.collect('selling time from')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput
            v-if="isHiddenField('sellingTime')"
            label="Selling time to"
          >
            <BaseTimePicker
              v-model="form.sellEndTime"
              v-validate="schema.sellEndTime"
              data-vv-name="selling time to"
              label="Selling time to"
              :error-messages="errors.collect('selling time to')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput
            v-if="isHiddenField('sellingDay')"
            label="Selling days"
          >
            <VSelect
              v-model="form.sellingDays"
              v-validate="schema.sellingDays"
              data-vv-name="selling days"
              label="Selling days"
              :error-messages="errors.collect('selling days')"
              :color="color"
              :items="dayOfWeek"
              item-text="name"
              item-value="code"
              multiple
            >
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text caption">
                  {{ form.sellingDays.length }} selected
                </span>
              </template>
            </VSelect>
          </BaseControlInput>
        </VFlex>
        <!-- End of Sell Conditions -->

        <!-- Stay Conditions -->
        <VFlex class="mb-4" xs12 sm6>
          <div class="title mb-2">Stay Conditions</div>

          <BaseControlInput label="Stay from">
            <BaseDatePicker
              v-model="form.stayStartDate"
              v-validate="schema.stayStartDate"
              data-vv-name="stay from"
              label="Stay from"
              :error-messages="errors.collect('stay from')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Stay to">
            <BaseDatePicker
              v-model="form.stayEndDate"
              v-validate="schema.stayEndDate"
              data-vv-name="stay to"
              label="Stay to"
              :error-messages="errors.collect('stay to')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Staying days">
            <VSelect
              v-model="form.stayingDays"
              v-validate="schema.stayingDays"
              data-vv-name="staying days"
              label="Staying days"
              :error-messages="errors.collect('staying days')"
              :color="color"
              :items="dayOfWeek"
              item-text="name"
              item-value="code"
              multiple
            >
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text caption">
                  {{ form.stayingDays.length }} selected
                </span>
              </template>
            </VSelect>
          </BaseControlInput>
        </VFlex>
        <!-- End of Stay Conditions -->

        <!-- Restrictions -->
        <VFlex class="mb-4" xs12 sm6>
          <div class="title mb-2">Restrictions</div>

          <!-- <BaseControlInput label="Room types">
            <VSelect
              v-model="form.roomTypes"
              v-validate="schema.roomTypes"
              data-vv-name="room types"
              label="Room types"
              :error-messages="errors.collect('room types')"
              :color="color"
              :items="roomTypes"
              item-text="name"
              item-value="id"
              multiple
            >
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text caption">
                  {{ form.roomTypes.length }} selected
                </span>
              </template>
            </VSelect>
          </BaseControlInput> -->

          <BaseControlInput label="Rate Plans">
            <VSelect
              v-model="form.ratePlans"
              v-validate="schema.ratePlans"
              data-vv-name="rate plans"
              label="Rate Plans"
              :error-messages="errors.collect('rate plans')"
              :color="color"
              :items="ratePlans"
              item-text="name"
              item-value="id"
              multiple
            >
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text caption">
                  {{ form.ratePlans.length }} selected
                </span>
              </template>
            </VSelect>
          </BaseControlInput>

          <BaseControlInput label="Cancellation">
            <VAutocomplete
              v-model="form.cancellationId"
              v-validate="schema.cancellationId"
              data-vv-name="cancellation"
              label="Cancellation"
              :error-messages="errors.collect('cancellation')"
              :color="color"
              :items="cancellations"
              item-text="name"
              item-value="id"
            />
          </BaseControlInput>

          <BaseControlInput label="Minimal LOS">
            <VTextField
              v-model="form.minimalNightStay"
              v-validate="schema.minimalNightStay"
              data-vv-name="minimal LOS"
              label="Minimal LOS"
              suffix="day(s)"
              :error-messages="errors.collect('minimal LOS')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Maximal LOS">
            <VTextField
              v-model="form.maximalNightStay"
              v-validate="schema.maximalNightStay"
              data-vv-name="maximal LOS"
              label="Maximal LOS"
              suffix="day(s)"
              :error-messages="errors.collect('maximal LOS')"
              :color="color"
            />
          </BaseControlInput>
        </VFlex>
        <!-- End of Restrictions -->

        <!-- Conditions -->
        <VFlex class="mb-4" xs12 sm6>
          <div class="title mb-2">Conditions</div>

          <BaseControlInput
            v-if="isHiddenField('minAdvance')"
            label="Minimal book in advance"
          >
            <VTextField
              v-model="form.minimalAdvancePurchase"
              v-validate="schema.minimalAdvancePurchase"
              data-vv-name="minimal book in advance"
              label="Minimal book in advance"
              suffix="days"
              :error-messages="errors.collect('minimal book in advance')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput
            v-if="isHiddenField('maxAdvance')"
            label="Maximal book in advance"
          >
            <VTextField
              v-model="form.maximalAdvancePurchase"
              v-validate="schema.maximalAdvancePurchase"
              data-vv-name="maximal book in advance"
              label="Maximal book in advance"
              suffix="days"
              :error-messages="errors.collect('maximal book in advance')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Discount amount">
            <VTextField
              v-model="form.percentDiscount"
              v-validate="schema.percentDiscount"
              data-vv-name="discount amount"
              label="Discount amount"
              suffix="%"
              :error-messages="errors.collect('discount amount')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Show promotion on mobile only">
            <VAutocomplete
              v-model="form.mobileOnly"
              label="Show promotion on mobile only"
              color="gold"
              :items="dropdown.switch"
            />
          </BaseControlInput>

          <BaseControlInput label="Promotion code">
            <VTextField
              v-model="form.promotionCode"
              label="Promotion code"
              :color="color"
            />
          </BaseControlInput>
        </VFlex>
        <!-- End of Conditions -->
      </VLayout>

      <template slot="card-actions-right">
        <VBtn depressed :disabled="loading" :to="{ name: 'promotion' }">
          Cancel
        </VBtn>

        <VBtn
          :color="color"
          :loading="loading"
          :disabled="!!errors.count()"
          type="submit"
          depressed
          :dark="!errors.count()"
        >
          {{ text.submit }}
        </VBtn>
      </template>
    </BaseView>
  </VForm>
</template>
